import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { HeatmapViewer } from './Heatmaps';

const RUN_LIMIT_OPTIONS = [1, 2, 5, 10, 20, 50];

/**
 * Shared zone heatmap display component.
 * Handles: data fetching, loading state, stats bar, device/CL meta,
 * color metric selector, run limit, camera persistence, and HeatmapViewer.
 *
 * Used by both the Areas tab and Quest Performance tab.
 *
 * @param {function} props.fetchHeatmapData - async (runLimit) => { capturePoints, totalPoints, filesLoaded }
 * @param {function} props.fetchMapConfig - async () => { bounds, imageUrl } | null
 * @param {object} props.api - API instance (for map image URL resolution)
 * @param {string} props.zoneName - Zone name (for map image URL resolution)
 * @param {number} [props.defaultRunLimit=10] - Initial run limit
 */
function ZoneHeatmapDisplay({ fetchHeatmapData, fetchMapConfig, api, zoneName, defaultRunLimit = 10 }) {
  const [heatmapData, setHeatmapData] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [colorMetric, setColorMetric] = useState('framerate');
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [runLimit, setRunLimit] = useState(defaultRunLimit);
  const [evaluators, setEvaluators] = useState([]);
  const viewerContainerRef = useRef(null);
  const savedCameraStateRef = useRef(null);

  // Fetch evaluators once
  useEffect(() => {
    if (!api) return;
    api.getEvaluators().then(data => setEvaluators(data.evaluators || [])).catch(() => {});
  }, [api]);

  // Resolve active evaluator from colorMetric
  const activeEvaluator = useMemo(() => {
    if (!colorMetric?.startsWith('eval:')) return null;
    const id = colorMetric.slice(5);
    return evaluators.find(e => e.id === id) || null;
  }, [colorMetric, evaluators]);

  // Stable reference to fetchHeatmapData that includes runLimit
  const fetchWithLimit = useCallback(
    () => fetchHeatmapData(runLimit),
    [fetchHeatmapData, runLimit]
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const [result, mapResult] = await Promise.all([
          fetchWithLimit(),
          fetchMapConfig ? fetchMapConfig().catch(() => null) : Promise.resolve(null)
        ]);
        if (cancelled) return;
        setHeatmapData(result);
        if (mapResult?.bounds && mapResult?.imageUrl) {
          let imageUrl = mapResult.imageUrl;
          if (imageUrl.startsWith('/') && api && zoneName) {
            try {
              imageUrl = await api.getHeatmapMapImage(zoneName);
            } catch { imageUrl = null; }
          }
          if (imageUrl) {
            setMapConfig({
              imageUrl,
              minX: mapResult.bounds.minX,
              maxX: mapResult.bounds.maxX,
              minZ: mapResult.bounds.minZ,
              maxZ: mapResult.bounds.maxZ
            });
          }
        }
      } catch (err) {
        console.error('Failed to load heatmap:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [fetchWithLimit, fetchMapConfig, api, zoneName]);

  // Compare mode: filter data by selected CL
  const [compareMode, setCompareMode] = useState(false);
  const [leftCL, setLeftCL] = useState(null);
  const [rightCL, setRightCL] = useState(null);
  const leftViewerRef = useRef(null);
  const rightViewerRef = useRef(null);
  const [leftSelectedPoints, setLeftSelectedPoints] = useState([]);
  const [rightSelectedPoints, setRightSelectedPoints] = useState([]);
  const sharedCameraState = useRef({
    position: null, target: null, frustumSize: null, zoom: null,
    lastUpdateTime: 0, lastUpdateSource: null
  });

  // Available CLs from the data
  const availableCLs = useMemo(() => {
    if (!heatmapData?.capturePoints) return [];
    return [...new Set(heatmapData.capturePoints.map(p => p.changelist).filter(Boolean))].sort((a, b) => b - a);
  }, [heatmapData]);

  // Auto-select CLs when entering compare mode
  useEffect(() => {
    if (compareMode && availableCLs.length >= 2) {
      if (!leftCL) setLeftCL(availableCLs[0]);
      if (!rightCL) setRightCL(availableCLs[1]);
    }
  }, [compareMode, availableCLs, leftCL, rightCL]);

  // Filter data by CL for compare mode
  const leftData = useMemo(() => {
    if (!heatmapData || !leftCL) return null;
    const points = heatmapData.capturePoints.filter(p => String(p.changelist) === String(leftCL));
    return { ...heatmapData, capturePoints: points, totalPoints: points.length };
  }, [heatmapData, leftCL]);

  const rightData = useMemo(() => {
    if (!heatmapData || !rightCL) return null;
    const points = heatmapData.capturePoints.filter(p => String(p.changelist) === String(rightCL));
    return { ...heatmapData, capturePoints: points, totalPoints: points.length };
  }, [heatmapData, rightCL]);

  // Shared bounds for compare mode so both viewers have the same spatial reference
  const sharedBounds = useMemo(() => {
    if (!compareMode || !leftData || !rightData) return null;

    const xVals = [];
    const zVals = [];
    const collectPoints = (data) => {
      if (!data?.capturePoints) return;
      for (const p of data.capturePoints) {
        if (p.position) {
          xVals.push(-p.position.x);
          zVals.push(p.position.z);
        }
      }
    };
    collectPoints(leftData);
    collectPoints(rightData);

    if (xVals.length === 0) return null;

    const filterOutliers = (values) => {
      if (values.length < 4) return { min: Math.min(...values), max: Math.max(...values) };
      const sorted = [...values].sort((a, b) => a - b);
      const lo = sorted[Math.floor(sorted.length * 0.05)];
      const hi = sorted[Math.floor(sorted.length * 0.95)];
      const span = hi - lo || 1;
      return { min: lo - span * 0.2, max: hi + span * 0.2 };
    };

    const xRange = filterOutliers(xVals);
    const zRange = filterOutliers(zVals);

    const bounds = {
      minX: xRange.min, maxX: xRange.max,
      minY: Infinity, maxY: -Infinity,
      minZ: zRange.min, maxZ: zRange.max
    };

    for (const data of [leftData, rightData]) {
      if (!data?.capturePoints) continue;
      for (const p of data.capturePoints) {
        if (p.position) {
          bounds.minY = Math.min(bounds.minY, p.position.y);
          bounds.maxY = Math.max(bounds.maxY, p.position.y);
        }
      }
    }
    if (!isFinite(bounds.minY)) { bounds.minY = 0; bounds.maxY = 100; }

    return bounds;
  }, [compareMode, leftData, rightData]);

  if (loading) {
    return <div className="zone-heatmap-loading"><div className="loading-spinner small"></div></div>;
  }

  if (!heatmapData || heatmapData.totalPoints === 0) {
    return <div className="zone-heatmap-empty">No heatmap data for this zone.</div>;
  }

  const points = heatmapData.capturePoints;
  const fpsList = points.map(p => p.framerate).filter(v => v > 0);
  const avgFps = fpsList.length > 0 ? (fpsList.reduce((a, b) => a + b, 0) / fpsList.length).toFixed(1) : 'N/A';
  const minFps = fpsList.length > 0 ? Math.min(...fpsList).toFixed(1) : 'N/A';
  const maxFps = fpsList.length > 0 ? Math.max(...fpsList).toFixed(1) : 'N/A';

  const gpuList = points.map(p => p.gpuTime).filter(v => v > 0);
  const avgGpu = gpuList.length > 0 ? (gpuList.reduce((a, b) => a + b, 0) / gpuList.length).toFixed(2) : 'N/A';

  const memList = points.map(p => p.currentMemory).filter(v => v > 0);
  const avgMem = memList.length > 0 ? (memList.reduce((a, b) => a + b, 0) / memList.length).toFixed(0) : 'N/A';

  const devices = [...new Set(points.map(p => p.device).filter(Boolean))];
  const cls = [...new Set(points.map(p => p.changelist).filter(Boolean))];

  return (
    <div className="zone-heatmap-detail">
      <div className="zone-heatmap-stats">
        <div className="stat">
          <span className="stat-label">Points</span>
          <span className="stat-value">{heatmapData.totalPoints}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Runs</span>
          <span className="stat-value">{heatmapData.filesLoaded}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Avg FPS</span>
          <span className="stat-value">{avgFps}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Min FPS</span>
          <span className="stat-value">{minFps}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Max FPS</span>
          <span className="stat-value">{maxFps}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Avg GPU</span>
          <span className="stat-value">{avgGpu}ms</span>
        </div>
        <div className="stat">
          <span className="stat-label">Avg Memory</span>
          <span className="stat-value">{avgMem}MB</span>
        </div>
      </div>
      {devices.length > 0 && (
        <div className="zone-heatmap-meta">
          <span className="meta-label">Devices:</span> {devices.join(', ')}
          {cls.length > 0 && (<><span className="meta-label"> CLs:</span> {cls.join(', ')}</>)}
        </div>
      )}
      <div className="zone-heatmap-controls">
        <label className="metric-label">Color by:</label>
        <select value={colorMetric} onChange={e => setColorMetric(e.target.value)} className="metric-select">
          <option value="framerate">Framerate (FPS)</option>
          <option value="gpuFps">GPU (FPS)</option>
          <option value="cpuFps">CPU (FPS)</option>
          <option value="drawCalls">Draw Calls</option>
          <option value="batches">Batches</option>
          <option value="triangles">Triangles</option>
          <option value="vertices">Vertices</option>
          {evaluators.length > 0 && <option disabled>── Evaluators ──</option>}
          {evaluators.map(ev => (
            <option key={ev.id} value={`eval:${ev.id}`}>{ev.name}</option>
          ))}
        </select>
        <div className="file-limit-control">
          <label>Load runs:</label>
          <select
            value={runLimit}
            onChange={(e) => setRunLimit(Number(e.target.value))}
            disabled={compareMode}
          >
            {RUN_LIMIT_OPTIONS.map(n => (
              <option key={n} value={n}>{n} most recent</option>
            ))}
          </select>
        </div>
        {availableCLs.length >= 2 && (
          <button
            className={`compare-mode-toggle ${compareMode ? 'active' : ''}`}
            onClick={() => {
              setCompareMode(!compareMode);
              setLeftSelectedPoints([]);
              setRightSelectedPoints([]);
            }}
          >
            {compareMode ? 'Exit Compare' : 'Compare CLs'}
          </button>
        )}
      </div>

      {compareMode ? (
        <div className="zone-compare-mode">
          <div className="zone-compare-selectors">
            <div className="zone-compare-selector">
              <label>Left CL:</label>
              <select value={leftCL || ''} onChange={e => setLeftCL(e.target.value)}>
                {availableCLs.map(cl => (
                  <option key={cl} value={cl}>{cl} ({heatmapData.capturePoints.filter(p => String(p.changelist) === String(cl)).length} pts)</option>
                ))}
              </select>
            </div>
            <div className="zone-compare-selector">
              <label>Right CL:</label>
              <select value={rightCL || ''} onChange={e => setRightCL(e.target.value)}>
                {availableCLs.map(cl => (
                  <option key={cl} value={cl}>{cl} ({heatmapData.capturePoints.filter(p => String(p.changelist) === String(cl)).length} pts)</option>
                ))}
              </select>
            </div>
          </div>
          <div className="zone-compare-viewers">
            <div className="zone-compare-viewer">
              <div className="zone-compare-label">CL {leftCL} ({leftData?.totalPoints || 0} pts)</div>
              <div className="zone-heatmap-viewer" ref={leftViewerRef}>
                {leftData && leftData.totalPoints > 0 ? (
                  <HeatmapViewer
                    data={leftData}
                    containerRef={leftViewerRef}
                    mapConfig={mapConfig}
                    selectedPoints={leftSelectedPoints}
                    setSelectedPoints={setLeftSelectedPoints}
                    colorMetric={colorMetric}
                    activeEvaluator={activeEvaluator}
                    sharedBounds={sharedBounds}
                    syncCameraState={sharedCameraState}
                    viewerId="left"
                  />
                ) : (
                  <div className="zone-heatmap-empty">No data for CL {leftCL}</div>
                )}
              </div>
            </div>
            <div className="zone-compare-viewer">
              <div className="zone-compare-label">CL {rightCL} ({rightData?.totalPoints || 0} pts)</div>
              <div className="zone-heatmap-viewer" ref={rightViewerRef}>
                {rightData && rightData.totalPoints > 0 ? (
                  <HeatmapViewer
                    data={rightData}
                    containerRef={rightViewerRef}
                    mapConfig={mapConfig}
                    selectedPoints={rightSelectedPoints}
                    setSelectedPoints={setRightSelectedPoints}
                    colorMetric={colorMetric}
                    activeEvaluator={activeEvaluator}
                    sharedBounds={sharedBounds}
                    syncCameraState={sharedCameraState}
                    viewerId="right"
                  />
                ) : (
                  <div className="zone-heatmap-empty">No data for CL {rightCL}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="zone-heatmap-viewer" ref={viewerContainerRef}>
          <HeatmapViewer
            data={heatmapData}
            containerRef={viewerContainerRef}
            mapConfig={mapConfig}
            selectedPoints={selectedPoints}
            setSelectedPoints={setSelectedPoints}
            colorMetric={colorMetric}
            activeEvaluator={activeEvaluator}
            onCameraChange={(state) => { savedCameraStateRef.current = state; }}
            initialCameraState={savedCameraStateRef.current}
          />
          {!mapConfig && (
            <p className="no-map-note">
              No map image available for this zone.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ZoneHeatmapDisplay;
